import type { StepView } from '~/helpers/learner/session-view'
import { CalendarClock, CheckCircle2, CirclePlay, Lock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { formatMinutes, formatSeconds } from '~/helpers/learner/duration'
import { STEP_ACTION_KEYS, stepAction, stepScore, stepStatus } from '~/helpers/learner/session-view'
import { cn } from '~/lib/utils'
import ScoreValue from '~/pages/scorm/components/ScoreValue'
import { useLockReason } from '~/pages/scorm/components/useLockReason'

type IProps = {
  step: StepView
  /** Toutes les étapes du parcours : la raison d'un verrou peut nommer un autre module. */
  steps: StepView[]
  current: boolean
  onLaunch: (moduleId: string) => void
}

/** Une étape de la colonne du parcours : son état, sa durée, son score, son verrou et son bouton. */
function PathStepItem({ step, steps, current, onLaunch }: IProps) {
  const { t } = useTranslation()
  const lockReason = useLockReason()
  const status = stepStatus(step)
  const done = status === 'COMPLETED'
  // Relancer un module terminé, c'est le refaire ; rien si le parcours l'interdit (#84).
  const action = stepAction(step)
  const unlocked = step.unlocked === true
  const ready = step.module?.status === 'READY'
  // Durée estimée et temps passé sous deux libellés distincts, comme sur
  // l'accueil : un « 9 min » nu, une fois le module entamé, se lisait
  // comme la durée d'un module qui en annonce trente (#90).
  const estimated = formatMinutes(step.module?.durationMinutes, t)
  const spent = formatSeconds(step.myTracking?.totalTimeSeconds, t)
  const score = stepScore(step)

  const Icon = done
    ? CheckCircle2
    : unlocked
      ? CirclePlay
      : step.lockReason === 'SCHEDULED' ? CalendarClock : Lock

  return (
    <li
      className={cn(
        'rounded-xl border p-3 transition-colors',
        // Fond de l'étape en cours en `muted`, et non en accent : l'accent
        // suit la marque, et le texte secondaire y tombait à 2,4:1 en
        // sombre. Une étape verrouillée n'est pas non plus grisée par
        // opacité, qui ruinait le contraste : son icône et sa raison
        // disent déjà qu'elle est fermée (#89).
        // Bordure de l'étape en cours : la teinte de marque lisible, ≥ 3:1 sur le fond (RGAA 3.3).
        current ? 'border-primary-text bg-muted' : 'border-transparent hover:bg-muted/50',
      )}
      aria-current={current ? 'step' : undefined}
    >
      <div className="flex items-start gap-2.5">
        <Icon
          className={cn(
            'mt-0.5 size-4 shrink-0',
            done ? 'text-success-text' : unlocked ? 'text-foreground' : 'text-muted-foreground',
          )}
        />
        <div className="min-w-0 flex-1">
          <p className="flex items-baseline gap-1.5 text-sm">
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
              {step.position + 1}
              .
            </span>
            <span className={cn('min-w-0 break-words', !unlocked && 'text-muted-foreground')}>
              {step.module?.title ?? ''}
            </span>
            {/* L'icône dit l'état à l'œil ; le lecteur d'écran l'entend ici (RGAA 3.1). */}
            <span className="sr-only">
              {' — '}
              {t(done ? 'path.step.status.done' : unlocked ? 'path.step.status.available' : 'path.step.status.locked')}
            </span>
          </p>

          {(estimated || spent || score) && (
            <p className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
              {estimated && <span>{t('home.meta.estimated', { duration: estimated })}</span>}
              {spent && <span>{t('home.meta.spent', { duration: spent })}</span>}
              {score && (
                <span>
                  {t('common.score')}
                  {' : '}
                  <ScoreValue score={score} />
                </span>
              )}
              {score?.threshold != null && (
                <span>{t('home.meta.threshold', { threshold: score.threshold })}</span>
              )}
            </p>
          )}

          {!unlocked && (
            <p className="mt-1 text-xs text-muted-foreground">
              {lockReason(step, steps)}
            </p>
          )}

          {unlocked && step.module?.id && action && (
            <Button
              size="sm"
              variant={current ? 'default' : done ? 'ghost' : 'outline'}
              className="mt-2"
              disabled={!ready}
              onClick={() => onLaunch(step.module!.id)}
              aria-label={t('common.actionFor', { action: t(STEP_ACTION_KEYS[action]), name: step.module.title })}
            >
              {t(STEP_ACTION_KEYS[action])}
            </Button>
          )}
        </div>
      </div>
    </li>
  )
}

export default PathStepItem
